<template>
  <div>
    <div class="card">
      <div class="card-body">
        <!--
          Same order as the creation screen, so a booking reads the same way whether you are
          making it or reviewing it. Store mode keeps the store first: it scopes the item, whose
          product is needed to compute the store's slots.
        -->
        <BookingStepCard v-if="mode === 'store'" :label="$t('Store')">
          <StoreCard v-if="booking.store" :store="booking.store"></StoreCard>
          <BookingStepPlaceholder v-else :label="$t('No store selected')" :icon="Store" />
        </BookingStepCard>

        <BookingStepCard v-if="mode !== 'store'" :label="$t('Dates')">
          <DateCard :mode="mode" :booking="booking"></DateCard>
          <template #action>
            <BookingStepEditButton v-if="!readOnly" @click="showDateModal = true" />
          </template>
        </BookingStepCard>

        <BookingStepCard :label="$t('Item')">
          <EquipmentInfo v-if="booking.equipment" :item="booking.equipment" />
          <BookingStepPlaceholder v-else :label="$t('No item selected')" :icon="Package" />
          <template #action>
            <BookingStepEditButton v-if="!readOnly" @click="showEquipmentModal = true" />
          </template>
        </BookingStepCard>

        <BookingStepCard v-if="mode === 'store' && booking.equipment" :label="$t('Dates')">
          <DateCard :mode="mode" :booking="booking"></DateCard>
          <template #action>
            <BookingStepEditButton v-if="!readOnly" @click="showDateModal = true" />
          </template>
        </BookingStepCard>

        <BookingStepCard v-if="booking.equipment" :label="$t('Participant')" :with-separator="false">
          <RegistrationInfo v-if="booking.registration" :registration="booking.registration" />
          <BookingStepPlaceholder v-else :label="$t('No participant selected')" :icon="CircleUserRound" />
          <template #action>
            <BookingStepEditButton v-if="!readOnly" @click="showRegistrationModal = true" />
          </template>
        </BookingStepCard>
      </div>
    </div>

    <template v-if="!readOnly">
      <EquipmentDialog
        v-model:open="showEquipmentModal"
        :base-filters="equipmentFilters"
        :available-filters="equipmentFilterDefinitions"
        :product-metafields="productMetafields"
        :equipment-metafields="equipmentMetafields"
        with-availability
        :timezone="timezone"
        @select="onEquipmentSelected"
      />

      <DateDialog
        v-model:open="showDateModal"
        :mode="mode"
        :booking="booking"
        :stages="stages"
        :timezone="timezone"
        :default-start-time="bookingStartTime"
        :default-end-time="bookingEndTime"
        :api-url="apiUrl"
        @stage-range="stageDateRange"
        @stage-store-slot="stageStoreSlot"
        @stage-slot="stageSlot"
        @discard="discardPendingDates"
        @save="onDatesSaved"
      />

      <RegistrationDialog
        v-model:open="showRegistrationModal"
        :base-filters="registrationBaseFilters"
        :available-filters="registrationAvailableFilters"
        :add-url="registrationAddUrl"
        @select="onRegistrationSelected"
      />

      <BookingHiddenInputs
        :mode="mode"
        :equipment-id="selectedEquipment"
        :bookable-id="bookableId"
        :date-range="selectedDateRange"
        :registration-id="selectedRegistration"
      />

      <button type="submit" class="d-none" ref="submitButton">
        {{ $t('Save') }}
      </button>
    </template>
  </div>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue';
import { CircleUserRound, Package, Store } from "@lucide/vue";
import BookingStepCard from "@/components/booking/BookingStepCard.vue";
import BookingStepEditButton from "@/components/booking/BookingStepEditButton.vue";
import BookingStepPlaceholder from "@/components/booking/BookingStepPlaceholder.vue";
import BookingHiddenInputs from "@/components/booking/BookingHiddenInputs.vue";
import EquipmentDialog from "@/components/booking/dialogs/EquipmentDialog.vue";
import DateDialog from "@/components/booking/dialogs/DateDialog.vue";
import RegistrationDialog from "@/components/booking/dialogs/RegistrationDialog.vue";
import DateCard from "@/components/booking/DateCard.vue";
import StoreCard from "@/components/store/StoreCard.vue";
import RegistrationInfo from "@/components/registration/RegistrationInfo.vue";
import EquipmentInfo from "@/components/equipment/EquipmentInfo.vue";
import { useBookingDraft } from "@/composables/useBookingDraft";

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validate: (value) => ["slot", "date", "store"].includes(value)
  },
  timezone: {
    type: String,
    required: true
  },
  booking: {
    type: Object,
    default: null
  },
  bookingStartTime: {
    type: String,
    default: null
  },
  bookingEndTime: {
    type: String,
    default: null
  },
  stages: {
    type: Array,
    default: () => []
  },
  equipmentBaseFilters: {
    type: Object,
    default: () => ({})
  },
  equipmentAvailableFilters: {
    type: Array,
    default: () => []
  },
  productMetafields: {
    type: Array,
    default: () => []
  },
  equipmentMetafields: {
    type: Array,
    default: () => []
  },
  registrationBaseFilters: {
    type: Object,
    default: () => ({})
  },
  registrationAvailableFilters: {
    type: Array,
    default: () => []
  },
  registrationAddUrl: {
    type: String,
    default: null
  },
  apiUrl: {
    type: String,
    required: true
  },
  // Viewers without the booking management ability see the steps but cannot edit them.
  readOnly: {
    type: Boolean,
    default: false
  }
});

const {
  booking,
  selectedEquipment,
  selectedRegistration,
  selectedDateRange,
  bookableId,
  equipmentFilters,
  withAvailabilityDefault,
  setEquipment,
  setRegistration,
  stageDateRange,
  stageStoreSlot,
  stageSlot,
  commitDates,
  discardPendingDates
} = useBookingDraft({
  mode: props.mode,
  equipmentBaseFilters: props.equipmentBaseFilters,
  booking: props.booking
});

const showEquipmentModal = ref(false);
const showDateModal = ref(false);
const showRegistrationModal = ref(false);
const submitButton = ref(null);

// The equipment list opens already filtered on the booking's period; it stays an ordinary filter
// the user can widen or clear from the filter bar.
const equipmentFilterDefinitions = computed(
  () => withAvailabilityDefault(props.equipmentAvailableFilters)
);

/** Editing saves on every change; nextTick lets the hidden inputs update before submitting. */
async function submitForm() {
  await nextTick();
  submitButton.value?.click();
}

function onEquipmentSelected(equipment) {
  setEquipment(equipment);
  submitForm();
}

function onDatesSaved() {
  commitDates();
  submitForm();
}

function onRegistrationSelected(registration) {
  setRegistration(registration);
  submitForm();
}
</script>
